import React from 'react';
import { PlayCircle, Download, ExternalLink } from 'lucide-react';
import { mediaUrl } from '../services/api';

export const detectProvider = (url = '') => {
  if (/youtube\.com|youtu\.be/.test(url)) return 'youtube';
  if (/vimeo\.com/.test(url)) return 'vimeo';
  if (/^https?:\/\//i.test(url)) return 'link';
  return 'file';
};

const embedUrl = (url, provider) => {
  if (provider === 'youtube') {
    let id = '';
    const m1 = url.match(/[?&]v=([^&]+)/);
    const m2 = url.match(/youtu\.be\/([^?&]+)/);
    const m3 = url.match(/\/embed\/([^?&]+)/);
    id = (m1 && m1[1]) || (m2 && m2[1]) || (m3 && m3[1]) || '';
    return `https://www.youtube.com/embed/${id}`;
  }
  if (provider === 'vimeo') {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return `https://player.vimeo.com/video/${m ? m[1] : ''}`;
  }
  return url;
};

export const VideoView = ({ video, className = 'w-full aspect-video rounded-xl bg-black' }) => {
  if (!video?.url) return null;
  const provider = video.provider || detectProvider(video.url);
  const hasCaption = video.title || video.description;

  let media;
  if (provider === 'youtube' || provider === 'vimeo') {
    media = (
      <iframe
        src={embedUrl(video.url, provider)}
        className={className}
        title={video.title || 'video'}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  } else if (provider === 'link') {
    media = (
      <a
        href={video.url}
        target="_blank"
        rel="noreferrer"
        data-testid="video-external-link"
        className={`${className} flex flex-col items-center justify-center bfc-dark text-white gap-2 no-underline`}
        style={video.thumbnail_url ? { backgroundImage: `url(${mediaUrl(video.thumbnail_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
      >
        <PlayCircle className="w-12 h-12 text-red-500" />
        <span className="text-sm font-semibold flex items-center gap-1">Voir la vidéo <ExternalLink className="w-3.5 h-3.5" /></span>
      </a>
    );
  } else {
    media = (
      <video
        src={mediaUrl(video.url)}
        poster={video.thumbnail_url ? mediaUrl(video.thumbnail_url) : undefined}
        controls
        playsInline
        controlsList={video.allow_download ? undefined : 'nodownload'}
        className={`${className} object-cover`}
      />
    );
  }

  if (!hasCaption && !(provider === 'file' && video.allow_download)) return media;

  return (
    <div className="space-y-2">
      {media}
      {hasCaption && (
        <div>
          {video.title && <p className="text-sm font-semibold text-foreground">{video.title}</p>}
          {video.description && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{video.description}</p>}
        </div>
      )}
      {provider === 'file' && video.allow_download && (
        <a
          href={mediaUrl(video.url)}
          download
          data-testid="video-download-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700"
        >
          <Download className="w-4 h-4" />Télécharger la vidéo
        </a>
      )}
    </div>
  );
};
